import writeXlsxFile, { Cell, SheetData } from 'write-excel-file/browser';

import {
    EXCEL_ERROR_CODE,
    ExcelCellValue,
    ExcelColumnType,
    ExcelInputCellValue,
    NormalizedExcelSpec,
} from './excel-export.types';

/** Заголовки колонок выделяем жирным и светлой заливкой. */
const HEADER_BACKGROUND = '#F1F5F9';
/** Формат даты по умолчанию: библиотека требует формат для каждой `Date`-ячейки. */
const DEFAULT_DATE_FORMAT = 'DD.MM.YYYY';

const DEFAULT_FILE_NAME = 'выгрузка';
const DEFAULT_SHEET_NAME = 'Лист1';

const MAX_ROWS = 10_000;
const MAX_COLUMNS = 200;
const MAX_TOTAL_CELLS = 200_000;
const MAX_CELL_TEXT_LENGTH = 5_000;
const MAX_FILE_NAME_LENGTH = 80;
const MAX_FORMAT_LENGTH = 128;
const MIN_COLUMN_WIDTH = 4;
const MAX_COLUMN_WIDTH = 120;

const COLUMN_TYPES: readonly ExcelColumnType[] = ['auto', 'number', 'date', 'text'];

/** Excel запрещает эти символы в имени файла. */
const INVALID_FILE_NAME_CHARS = /[\\/:*?"<>|\u0000-\u001f]/g;
/** Excel запрещает эти символы в имени листа. */
const INVALID_SHEET_NAME_CHARS = /[\\/:*?[\]]/g;
const MAX_SHEET_NAME_LENGTH = 31;

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_DATE_TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d+))?$/;
const ISO_DATE_TIME_WITH_ZONE_PATTERN = /(?:Z|[+-]\d{2}:\d{2})$/i;
const DOT_DATE_PATTERN = /^(\d{2})\.(\d{2})\.(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/;
const PLAIN_NUMBER_PATTERN = /^-?\d+([.,]\d+)?$/;

/** Ошибка валидации аргументов тулзы: код уходит агенту, чтобы он поправил ввод. */
class ExcelSpecError extends Error {
    public constructor(public readonly code: string, message: string) {
        super(message);
        this.name = 'ExcelSpecError';
    }
}

/** Результат нормализации: либо готовый spec, либо структурированная ошибка. */
export type NormalizeExcelSpecResult =
    | { status: 'ok'; spec: NormalizedExcelSpec }
    | { status: 'error'; code: string; message: string };

/**
 * Проверяет и приводит аргументы агента к внутреннему виду.
 * Ошибки формулируются так, чтобы агент мог исправить вызов без уточнений.
 */
export function normalizeExcelSpec(args: Record<string, unknown>): NormalizeExcelSpecResult {
    try {
        return { status: 'ok', spec: buildSpec(args) };
    } catch (error) {
        if (error instanceof ExcelSpecError) {
            return { status: 'error', code: error.code, message: error.message };
        }

        throw error;
    }
}

/** Собирает `.xlsx` в памяти и возвращает Blob. */
export async function buildExcelBlob(spec: NormalizedExcelSpec): Promise<Blob> {
    const sheetData: SheetData = [];

    if (spec.columns.length > 0) {
        sheetData.push(
            spec.columns.map(column => ({
                value: column,
                fontWeight: 'bold',
                backgroundColor: HEADER_BACKGROUND,
            })),
        );
    }

    for (const row of spec.rows) {
        sheetData.push(row.map((value, columnIndex) => toSheetCell(value, spec.columnFormats[columnIndex] ?? null)));
    }

    return writeXlsxFile(sheetData, {
        sheet: spec.sheetName,
        dateFormat: DEFAULT_DATE_FORMAT,
        ...(spec.columnWidths.length > 0
            ? { columns: spec.columnWidths.map(width => ({ width })) }
            : {}),
    }).toBlob();
}

/**
 * Превращает нормализованное значение в ячейку библиотеки.
 * Формат применяем только к числам и датам: для строк библиотека разрешает лишь формат `@`.
 */
function toSheetCell(value: ExcelCellValue, format: string | null): Cell {
    if (value === null || value === undefined) {
        return null;
    }

    if (value instanceof Date) {
        return { value, type: Date, format: format ?? DEFAULT_DATE_FORMAT };
    }

    if (typeof value === 'number' && format) {
        return { value, type: Number, format };
    }

    return value;
}

function buildSpec(args: Record<string, unknown>): NormalizedExcelSpec {
    // `data` принимаем как синоним `rows` — агенту проще выбрать одно из двух.
    const rawRows = args['rows'] ?? args['data'];

    if (!Array.isArray(rawRows)) {
        throw new ExcelSpecError(
            EXCEL_ERROR_CODE.invalidArgs,
            'Параметр rows обязателен: это массив строк, каждая строка — массив значений (string | number | boolean | null).',
        );
    }

    if (rawRows.length === 0) {
        throw new ExcelSpecError(
            EXCEL_ERROR_CODE.invalidArgs,
            'Параметр rows пустой: передай хотя бы одну строку данных.',
        );
    }

    if (rawRows.length > MAX_ROWS) {
        throw new ExcelSpecError(
            EXCEL_ERROR_CODE.rowsTooLarge,
            `Слишком много строк: ${rawRows.length}. Максимум — ${MAX_ROWS}. Сократи выгрузку или разбей её на несколько файлов.`,
        );
    }

    const columns = parseColumns(args['columns']);
    const columnWidths = parseColumnWidths(args['columnWidths']);
    const columnFormats = parseColumnFormats(args['columnFormats']);
    const columnTypes = parseColumnTypes(args['columnTypes']);

    const validatedRows = rawRows.map((row, rowIndex): ExcelInputCellValue[] => {
        if (!Array.isArray(row)) {
            throw new ExcelSpecError(
                EXCEL_ERROR_CODE.invalidRows,
                `Строка ${rowIndex + 1} должна быть массивом значений, а не ${describeType(row)}.`,
            );
        }

        if (row.length > MAX_COLUMNS) {
            throw new ExcelSpecError(
                EXCEL_ERROR_CODE.tooManyColumns,
                `В строке ${rowIndex + 1} слишком много колонок: ${row.length}. Максимум — ${MAX_COLUMNS}.`,
            );
        }

        return row as ExcelInputCellValue[];
    });

    const columnCount = validatedRows.reduce(
        (max, row) => Math.max(max, row.length),
        columns.length,
    );

    if (columnCount > MAX_COLUMNS) {
        throw new ExcelSpecError(
            EXCEL_ERROR_CODE.tooManyColumns,
            `Слишком много колонок: ${columnCount}. Максимум — ${MAX_COLUMNS}.`,
        );
    }

    const totalCells = validatedRows.length * Math.max(columnCount, 1);

    if (totalCells > MAX_TOTAL_CELLS) {
        throw new ExcelSpecError(
            EXCEL_ERROR_CODE.tooLarge,
            `Слишком большой объём данных: ${validatedRows.length} строк × ${columnCount} колонок = ${totalCells} ячеек. Максимум — ${MAX_TOTAL_CELLS}. Сократи выгрузку.`,
        );
    }

    validateColumnFormats(columnFormats, columnTypes);

    const rows = validatedRows.map((row, rowIndex) =>
        row.map((cell, columnIndex) =>
            normalizeCell(cell, rowIndex, columnIndex, columnTypes[columnIndex] ?? 'auto'),
        ),
    );

    const sheetName = sanitizeSheetName(args['sheetName']);
    const fileName = buildFileName(args['fileName'], sheetName);

    return { fileName, sheetName, columns, rows, columnWidths, columnFormats, columnTypes, columnCount };
}

function normalizeCell(
    value: ExcelInputCellValue,
    rowIndex: number,
    columnIndex: number,
    columnType: ExcelColumnType,
): ExcelCellValue {
    const position = `строка ${rowIndex + 1}, колонка ${columnIndex + 1}`;

    if (value === null || value === undefined) {
        return null;
    }

    if (typeof value === 'object') {
        throw new ExcelSpecError(
            EXCEL_ERROR_CODE.unsupportedCellType,
            `Недопустимое значение ячейки (${position}): ${describeType(value)}. Поддерживаются только string, number, boolean и null — без вложенных объектов и массивов.`,
        );
    }

    if (typeof value === 'boolean') {
        return columnType === 'text' ? String(value) : value;
    }

    if (typeof value === 'number') {
        if (!Number.isFinite(value)) {
            return null;
        }

        if (columnType === 'date') {
            throw new ExcelSpecError(
                EXCEL_ERROR_CODE.invalidDateValue,
                `Ячейка (${position}) помечена как дата, но содержит число ${value}. Передай дату строкой: "2026-10-02" или "02.10.2026".`,
            );
        }

        return columnType === 'text' ? String(value) : value;
    }

    if (typeof value === 'string') {
        if (value.length > MAX_CELL_TEXT_LENGTH) {
            throw new ExcelSpecError(
                EXCEL_ERROR_CODE.cellTooLong,
                `Текст в ячейке (${position}) длиннее ${MAX_CELL_TEXT_LENGTH} символов. Сократи значение.`,
            );
        }

        if (columnType === 'date') {
            return parseDateCell(value, position);
        }

        if (columnType === 'number') {
            return parseNumberCell(value, position);
        }

        return value;
    }

    throw new ExcelSpecError(
        EXCEL_ERROR_CODE.unsupportedCellType,
        `Недопустимое значение ячейки (${position}): ${describeType(value)}. Поддерживаются только string, number, boolean и null.`,
    );
}

/**
 * Разбирает дату в ячейке.
 * Даты собираем через `Date.UTC`, потому что библиотека считает Excel-сериал от UTC-времени:
 * иначе локальная таймзона сдвигала бы отображаемую дату/время.
 */
function parseDateCell(value: string, position: string): Date {
    const trimmed = value.trim();

    const isoDateMatch = ISO_DATE_PATTERN.exec(trimmed);

    if (isoDateMatch) {
        return new Date(Date.UTC(Number(isoDateMatch[1]), Number(isoDateMatch[2]) - 1, Number(isoDateMatch[3])));
    }

    const isoDateTimeMatch = ISO_DATE_TIME_PATTERN.exec(trimmed);

    if (isoDateTimeMatch) {
        return new Date(Date.UTC(
            Number(isoDateTimeMatch[1]),
            Number(isoDateTimeMatch[2]) - 1,
            Number(isoDateTimeMatch[3]),
            Number(isoDateTimeMatch[4]),
            Number(isoDateTimeMatch[5]),
            Number(isoDateTimeMatch[6] ?? 0),
            Number((isoDateTimeMatch[7] ?? '0').slice(0, 3).padEnd(3, '0')),
        ));
    }

    const dotMatch = DOT_DATE_PATTERN.exec(trimmed);

    if (dotMatch) {
        return new Date(Date.UTC(
            Number(dotMatch[3]),
            Number(dotMatch[2]) - 1,
            Number(dotMatch[1]),
            Number(dotMatch[4] ?? 0),
            Number(dotMatch[5] ?? 0),
            Number(dotMatch[6] ?? 0),
        ));
    }

    // ISO с явной таймзоной разбирает сам `Date`, дальше он идёт в сериал как есть.
    if (ISO_DATE_TIME_WITH_ZONE_PATTERN.test(trimmed)) {
        const parsed = new Date(trimmed);

        if (!Number.isNaN(parsed.getTime())) {
            return parsed;
        }
    }

    throw new ExcelSpecError(
        EXCEL_ERROR_CODE.invalidDateValue,
        `Не удалось разобрать дату в ячейке (${position}): "${value}". Поддерживаются форматы "2026-10-02", "02.10.2026", "02.10.2026 14:30" и ISO с временем.`,
    );
}

function parseNumberCell(value: string, position: string): number {
    // Пробелы (в т.ч. неразрывные) используем как разделители тысяч.
    const normalized = value.replace(/[\s\u00a0]/g, '').replace(',', '.');

    if (!PLAIN_NUMBER_PATTERN.test(normalized)) {
        throw new ExcelSpecError(
            EXCEL_ERROR_CODE.invalidNumberValue,
            `Не удалось разобрать число в ячейке (${position}): "${value}". Передай число числом или строкой без лишних символов.`,
        );
    }

    return Number(normalized);
}

function parseColumns(raw: unknown): string[] {
    if (raw === undefined || raw === null) {
        return [];
    }

    if (!Array.isArray(raw)) {
        throw new ExcelSpecError(
            EXCEL_ERROR_CODE.invalidColumns,
            'Параметр columns должен быть массивом строк-заголовков.',
        );
    }

    if (raw.length > MAX_COLUMNS) {
        throw new ExcelSpecError(
            EXCEL_ERROR_CODE.tooManyColumns,
            `Слишком много заголовков: ${raw.length}. Максимум — ${MAX_COLUMNS}.`,
        );
    }

    return raw.map((value, index) => {
        if (typeof value === 'string') {
            return value;
        }

        if (typeof value === 'number' && Number.isFinite(value)) {
            return String(value);
        }

        throw new ExcelSpecError(
            EXCEL_ERROR_CODE.invalidColumns,
            `Заголовок колонки ${index + 1} должен быть строкой, а не ${describeType(value)}.`,
        );
    });
}

function parseColumnWidths(raw: unknown): number[] {
    if (raw === undefined || raw === null) {
        return [];
    }

    if (!Array.isArray(raw)) {
        throw new ExcelSpecError(
            EXCEL_ERROR_CODE.invalidColumnWidths,
            'Параметр columnWidths должен быть массивом чисел.',
        );
    }

    return raw.map((value, index) => {
        if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
            throw new ExcelSpecError(
                EXCEL_ERROR_CODE.invalidColumnWidths,
                `Ширина колонки ${index + 1} должна быть положительным числом.`,
            );
        }

        return Math.min(Math.max(value, MIN_COLUMN_WIDTH), MAX_COLUMN_WIDTH);
    });
}

function parseColumnFormats(raw: unknown): (string | null)[] {
    if (raw === undefined || raw === null) {
        return [];
    }

    if (!Array.isArray(raw)) {
        throw new ExcelSpecError(
            EXCEL_ERROR_CODE.invalidColumnFormats,
            'Параметр columnFormats должен быть массивом Excel-форматов (или null) по колонкам.',
        );
    }

    if (raw.length > MAX_COLUMNS) {
        throw new ExcelSpecError(
            EXCEL_ERROR_CODE.tooManyColumns,
            `Слишком много форматов: ${raw.length}. Максимум — ${MAX_COLUMNS}.`,
        );
    }

    return raw.map((value, index) => {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        if (typeof value !== 'string') {
            throw new ExcelSpecError(
                EXCEL_ERROR_CODE.invalidColumnFormats,
                `Формат колонки ${index + 1} должен быть строкой Excel-формата (например "#,##0.00" или "DD.MM.YYYY").`,
            );
        }

        const format = value.trim();

        if (!format || format.length > MAX_FORMAT_LENGTH) {
            throw new ExcelSpecError(
                EXCEL_ERROR_CODE.invalidColumnFormats,
                `Формат колонки ${index + 1} пустой или длиннее ${MAX_FORMAT_LENGTH} символов.`,
            );
        }

        return format;
    });
}

function parseColumnTypes(raw: unknown): ExcelColumnType[] {
    if (raw === undefined || raw === null) {
        return [];
    }

    if (!Array.isArray(raw)) {
        throw new ExcelSpecError(
            EXCEL_ERROR_CODE.invalidColumnTypes,
            'Параметр columnTypes должен быть массивом типов колонок.',
        );
    }

    if (raw.length > MAX_COLUMNS) {
        throw new ExcelSpecError(
            EXCEL_ERROR_CODE.tooManyColumns,
            `Слишком много типов колонок: ${raw.length}. Максимум — ${MAX_COLUMNS}.`,
        );
    }

    return raw.map((value, index) => {
        if (value === null || value === undefined || value === '') {
            return 'auto';
        }

        const type = typeof value === 'string' ? value.trim().toLowerCase() : '';

        // `string` — привычный алиас текстового типа.
        if (type === 'string') {
            return 'text';
        }

        if ((COLUMN_TYPES as readonly string[]).includes(type)) {
            return type as ExcelColumnType;
        }

        throw new ExcelSpecError(
            EXCEL_ERROR_CODE.invalidColumnTypes,
            `Неизвестный тип колонки ${index + 1}: "${String(value)}". Допустимые типы: ${COLUMN_TYPES.join(', ')} (string = text).`,
        );
    });
}

/** Формат на текстовую колонку библиотека не примет — отсекаем это на валидации. */
function validateColumnFormats(
    columnFormats: (string | null)[],
    columnTypes: ExcelColumnType[],
): void {
    columnFormats.forEach((format, index) => {
        if (!format || format === '@') {
            return;
        }

        if ((columnTypes[index] ?? 'auto') === 'text') {
            throw new ExcelSpecError(
                EXCEL_ERROR_CODE.invalidFormatForColumn,
                `Для текстовой колонки ${index + 1} формат "${format}" не поддерживается: убери формат или убери тип text/string.`,
            );
        }
    });
}

function sanitizeSheetName(raw: unknown): string {
    const source = typeof raw === 'string' ? raw : '';
    const sanitized = source
        .replace(INVALID_SHEET_NAME_CHARS, ' ')
        .replace(/[\u0000-\u001f]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, MAX_SHEET_NAME_LENGTH);

    return sanitized || DEFAULT_SHEET_NAME;
}

function buildFileName(raw: unknown, sheetName: string): string {
    const source = typeof raw === 'string' && raw.trim()
        ? raw
        : (sheetName !== DEFAULT_SHEET_NAME ? sheetName : DEFAULT_FILE_NAME);

    const withoutExtension = source.replace(/\.xlsx$/i, '');
    const sanitized = withoutExtension
        .replace(INVALID_FILE_NAME_CHARS, '-')
        .replace(/\s+/g, ' ')
        .replace(/[- ]{2,}/g, '-')
        .replace(/^[.\-\s]+|[.\-\s]+$/g, '')
        .slice(0, MAX_FILE_NAME_LENGTH)
        .trim();

    return `${sanitized || DEFAULT_FILE_NAME}.xlsx`;
}

function describeType(value: unknown): string {
    if (value === null) {
        return 'null';
    }

    if (Array.isArray(value)) {
        return 'массив';
    }

    if (typeof value === 'object') {
        return 'объект';
    }

    return typeof value;
}
